import type { Category, Product } from "@/lib/products";
import { normalizeCatalogApiBase } from "./catalog-api-url.mjs";

type CatalogRef = string | { _id?: string; name?: string; slug?: string; isActive?: boolean } | null;
type PublishedModel = {
  _id?: string;
  slug: string;
  name?: string;
  isActive?: boolean;
  isPublished?: boolean;
  categoryId?: CatalogRef;
  subcategoryId?: CatalogRef;
  productId?: CatalogRef;
  heroDescription?: string;
  image?: { url?: string; alt?: string };
  imageGallery?: { url?: string; alt?: string }[];
  galleryImages?: { url?: string; alt?: string }[];
  specifications?: { groupName?: string; name?: string; value?: string; unit?: string }[];
  websiteContent?: {
    applications?: { title?: string; description?: string; items?: { title: string; description?: string }[] };
    support?: { title?: string; description?: string; items?: { t?: string; d?: string; title?: string; description?: string }[] };
    badges?: string[];
    availability?: string;
    popularity?: number;
    capacity?: number;
  };
  websiteMedia?: { thumbnail?: { url?: string; alt?: string }; gallery?: { url: string; alt?: string }[] };
  thumbnailImage?: { url?: string; alt?: string };
  content?: {
    heroSection?: { productName?: string; tagline?: string; description?: string; media?: { url?: string; alt?: string }[] };
    productOverview?: { title?: string; subtitle?: string; description?: string; brochureLink?: string; specificationHighlights?: { title?: string; value?: string; unit?: string; icon?: string }[] };
    keyFeaturesContent?: { features?: { title?: string; description?: string; featuredImage?: { url?: string }; bulletPoints?: string[] }[] };
    technicalSpecsContent?: { categories?: { name?: string; parameters?: { name?: string; value?: string; unit?: string }[] }[] };
  };
  seo?: { metaTitle?: string; metaDescription?: string; keywords?: string[] };
};
type PublishedCategory = {
  _id?: string;
  name: string;
  slug: string;
  title?: string;
  subtitle?: string;
  description?: string;
  mainImage?: string;
  thumbnailImage?: { url?: string; alt?: string };
  isActive?: boolean;
};

const asRef = (ref: CatalogRef) => typeof ref === "object" && ref ? ref : undefined;
const refSlug = (ref: CatalogRef) => asRef(ref)?.slug;
const refName = (ref: CatalogRef) => asRef(ref)?.name;
function categorySlugFor(record: PublishedModel): string {
  const category = asRef(record.categoryId);
  if (!category) return "";
  return category.slug || "";
}
const fallbackImage: Product["image"] = { url: "/images/product-placeholder.png", alt: "Equipment model" };
const CATALOG_REVALIDATE_SECONDS = 5 * 60;

function catalogFetchOptions() {
  // Local admin edits should be visible immediately during development. Keep
  // tagged ISR in production so catalog mutations can revalidate the website.
  return process.env.NODE_ENV === "development"
    ? { cache: "no-store" as const }
    : { next: { revalidate: CATALOG_REVALIDATE_SECONDS, tags: ["kasai-catalog"] } };
}

function toProduct(record: PublishedModel, category?: PublishedCategory): Product {
  const website = record.websiteContent ?? {};
  const content = record.content ?? {};
  const hero = content.heroSection ?? {};
  const overview = content.productOverview ?? {};
  const cover = record.websiteMedia?.thumbnail?.url
    ? record.websiteMedia.thumbnail
    : record.thumbnailImage?.url ? record.thumbnailImage : record.image?.url ? record.image : undefined;
  const gallery = record.websiteMedia?.gallery?.length
    ? record.websiteMedia.gallery.map((image) => image.url)
    : record.imageGallery?.length
      ? record.imageGallery.map((image) => image.url || "").filter(Boolean)
      : record.galleryImages?.length
        ? record.galleryImages.map((image) => image.url || "").filter(Boolean)
        : hero.media?.map((image) => image.url || "").filter(Boolean);
  const features = content.keyFeaturesContent?.features?.map((feature) => ({
    title: feature.title || "",
    desc: feature.description || "",
    image: feature.featuredImage?.url,
    bullets: feature.bulletPoints || [],
  })).filter((feature) => feature.title || feature.desc || feature.bullets.length);
  const contentSpecs = Object.fromEntries((content.technicalSpecsContent?.categories || []).map((group) => [
    group.name || "Specifications",
    (group.parameters || []).map((parameter) => ({
      l: parameter.name || "",
      v: [parameter.value, parameter.unit].filter(Boolean).join(" "),
    })).filter((parameter) => parameter.l || parameter.v),
  ]));
  const legacySpecs = (record.specifications || []).reduce<Record<string, { l: string; v: string }[]>>((groups, spec) => {
    const name = spec.groupName || "Specifications";
    (groups[name] ||= []).push({ l: spec.name || "", v: [spec.value, spec.unit].filter(Boolean).join(" ") });
    return groups;
  }, {});
  const specs = Object.keys(contentSpecs).length ? contentSpecs : legacySpecs;
  const availability = ["In Stock", "Order to Build", "Limited"].includes(website.availability || "")
    ? website.availability as Product["availability"]
    : "Order to Build";
  const categorySlug = categorySlugFor(record);
  const productName = overview.title || hero.productName || record.name || record.slug.toUpperCase();

  return {
    id: record.slug || "",
    model: record.slug?.toUpperCase() || "",
    categorySlug,
    subcategory: refName(record.subcategoryId) || "",
    name: productName,
    description: overview.description || hero.description || record.heroDescription || "",
    image: cover?.url ? { url: cover.url, alt: cover.alt || productName } : fallbackImage,
    imageGallery: gallery,
    brochureUrl: overview.brochureLink,
    stats: overview.specificationHighlights?.length
      ? overview.specificationHighlights.map((item) => ({ label: item.title || "", value: item.value || "", unit: item.unit || "", icon: item.icon }))
      : [],
    features,
    specs,
    applications: website.applications || [],
    support: website.support,
    badges: website.badges,
    availability,
    popularity: Number(website.popularity ?? 0),
    capacity: Number(website.capacity ?? 0),
    status: record.isActive === true && record.isPublished === true,
    seoTitle: record.seo?.metaTitle,
    seoDescription: record.seo?.metaDescription,
  };
}

async function getCatalogFeed(): Promise<PublishedModel[] | undefined> {
  const configuredApiUrl = process.env.CATALOG_API_URL;
  if (!configuredApiUrl) return undefined;
  try {
    const apiUrl = normalizeCatalogApiBase(configuredApiUrl);
    const response = await fetch(`${apiUrl}/catalog/public/models`, catalogFetchOptions());
    if (!response.ok) {
      console.error(`[catalog] Published model feed returned HTTP ${response.status}.`);
      return undefined;
    }
    const result = await response.json() as { data?: PublishedModel[] };
    if (!Array.isArray(result.data)) {
      console.error('[catalog] Published model feed response did not contain a data array.');
      return undefined;
    }
    return result.data;
  } catch (error) {
    console.error('[catalog] Failed to fetch the published model feed; using bundled product fallback.', error);
    return undefined;
  }
}

export async function getPublishedCategories(): Promise<Category[]> {
  const configuredApiUrl = process.env.CATALOG_API_URL;
  if (!configuredApiUrl) throw new Error("CATALOG_API_URL is required to load the admin catalog.");
  const apiUrl = normalizeCatalogApiBase(configuredApiUrl);
  // This public, active-category endpoint is available on the currently deployed
  // backend as well as newer builds; the /catalog/public/categories alias may lag.
  const response = await fetch(`${apiUrl}/catalog/categories?isActive=true&limit=100`, {
    ...catalogFetchOptions(),
  });
  if (!response.ok) throw new Error(`Active category feed returned HTTP ${response.status}.`);
  const result = await response.json() as { data?: PublishedCategory[] };
  if (!Array.isArray(result.data)) throw new Error("Active category feed response did not contain a data array.");
  return result.data.map((record) => ({
    slug: record.slug,
    name: record.title || record.name,
    subtitle: record.subtitle || "",
    description: record.description || "",
    image: record.thumbnailImage?.url || record.mainImage || "",
    subcategories: [],
    active: record.isActive === true,
  }));
}

export async function getPublishedProduct(id: string): Promise<Product | undefined> {
  const feed = await getCatalogFeed();
  if (!feed) return undefined;
  const record = feed.find((item) => item.slug?.toLowerCase() === id.toLowerCase());
  if (!record) return undefined;
  const categories = await getPublishedCategories();
  const category = categories.find((item) => item.slug === categorySlugFor(record));
  return toProduct(record, category);
}

export async function getPublishedProductsForCategory(slug: string): Promise<Product[]> {
  const feed = await getCatalogFeed();
  if (!feed) return [];
  const categoryRecords = feed.filter((record) => categorySlugFor(record) === slug);
  const categories = await getPublishedCategories();
  const category = categories.find((item) => item.slug === slug);
  if (!category) return [];
  return categoryRecords.map((record) => toProduct(record, category));
}

export async function getPublishedProductCategory(slug: string, productId: string): Promise<Category | undefined> {
  const categories = await getPublishedCategories();
  if (slug) return categories.find((category) => category.slug === slug);
  const product = await getPublishedProduct(productId);
  return product ? categories.find((category) => category.slug === product.categorySlug) : undefined;
}
